/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { I18n } from '@adp-arch/i18n';

import { type FunctionCallMessageUnit } from '../../../utils/fucntion-call/types';
import { isToolResponseFailed } from './tool-status';

type Todo = {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
};

// Only committed tool results can advance the visible plan. Partial streamed
// arguments or failed calls must not make work appear complete.
export function getHarnessPlan(
  units: FunctionCallMessageUnit[],
): Todo[] | undefined {
  for (let i = units.length - 1; i >= 0; i--) {
    const unit = units[i];
    if (!unit.apiResponse || isToolResponseFailed(unit.apiResponse.extra_info))
      continue;
    try {
      const parsed = JSON.parse(unit.llmOutput.content);
      const call = parsed.function ?? parsed;
      if (call.name !== 'write_todos') continue;
      const args =
        typeof call.arguments === 'string'
          ? JSON.parse(call.arguments)
          : call.arguments;
      if (!Array.isArray(args?.todos) || args.todos.length > 100) continue;
      if (
        !args.todos.every(
          (t: Todo) =>
            typeof t?.content === 'string' &&
            ['pending', 'in_progress', 'completed'].includes(t.status),
        )
      )
        continue;
      return args.todos;
    } catch {
      /* Incomplete streamed calls are not a plan update. */
    }
  }
}
export function HarnessPlan({ units }: { units: FunctionCallMessageUnit[] }) {
  const todos = getHarnessPlan(units);
  if (!todos?.length) return null;
  return (
    <section
      aria-label={I18n.t('harness_plan')}
      className="mb-3 rounded-lg border coz-stroke-primary p-3 coz-bg-plus"
      aria-live="polite"
    >
      <div className="font-medium mb-2">
        {I18n.t('harness_plan')} ·{' '}
        {todos.filter(t => t.status === 'completed').length}/{todos.length}
      </div>
      <ol className="flex flex-col gap-2 m-0 pl-5">
        {todos.map((todo, i) => (
          <li key={`${i}:${todo.content}`} className="text-xs leading-5">
            <span>{todo.content}</span>{' '}
            <span className="coz-fg-secondary">
              — {I18n.t(`harness_plan_${todo.status}`)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
